'use client';

import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React from 'react';

import { doTranslation } from '@/app/i18n/client';
import Button from '@/components/global/buttons/button';

import styles from '@/components/home-page/home-page-new-roads-to-travel.module.scss';
import nycToKingston from '@/public/assets/images/global/new-roads-to-travel/nyc-to-kingston.jpg';
import hudsonValley from '@/public/assets/images/global/new-roads-to-travel/hudson-valley.jpg';
import beach from '@/public/assets/images/global/new-roads-to-travel/beach.jpg';
import bus from '@/public/assets/images/global/new-roads-to-travel/bus.jpg';
import freeRide from '@/public/assets/images/global/static/free-rides.webp';

interface Props {
  lng: string;
}

const HomePageNewRoadsToTravel = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);
  const items = [
    {
      image: hudsonValley,
      headline: t('The Ultimate Hudson Valley Experience'),
      subHeadline: t('Leave the worries of navigating unfamiliar roads behind...'),
      link: '/experience/bus-tickets-to-hudson-valley-ny',
      alt: 'Bear Mountain Bridge in the Hudson Valley.'
    },
    {
      image: beach,
      headline: t('5 Beach Day Essentials'),
      subHeadline: t('All you want to do is lay out on a beach, take a dip in...'),
      link: '/expert/5-beach-day-essentials',
      alt: t('A photo of a woman in the water on a pool float.')
    },
    {
      image: nycToKingston,
      headline: t('Trailways and HVH perfect bus trip from NYC to Kingston NY'),
      subHeadline: t('Trailways is proud to team with Hudson Valley Happenings...'),
      link: '/experience/trailways-and-hudson-valley-happenings-perfect-bus-trip-from-nyc-to-kingston-ny',
      alt: t('The Pronkstilleven by Gaia mural on The Stuyvesant in Kingston, NY.')
    },
    {
      image: bus,
      headline: t('4 Bus Trip Essentials'),
      subHeadline: t('You have a long weekend; you want to get away, but you...'),
      link: '/expert/4-bus-trip-essentials',
      alt: t('A photo of travelers getting on a bus.')
    }
  ];
  const validItems = items.filter((item) => item.image && item.headline && item.link);

  if (validItems.length === 0) {
    return <></>;
  }

  return (
    <>
      {process.env.NEXT_PUBLIC_COUNTRY == 'us' && (
        <div className={styles['new-roads-to-travel']}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t('New Roads to Travel')}</h2>
            <p className={styles.subtitle}>
              {t(
                "Whether you're craving the buzz of the big city or the peace of the great outdoors, we've got you covered."
              )}
            </p>
          </div>
          <div className={styles.cards}>
            {validItems.map((item: any, index: number) => (
              // eslint-disable-next-line
              <a key={index} className={styles.row} href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${item.link}`}>
                <div className={styles.card}>
                  <Image
                    alt={item.alt}
                    className={styles.image}
                    height={206}
                    src={item.image}
                    width={271}
                    style={{ minWidth: '271px', height: 'auto' }}
                  />
                  <div className={styles.content}>
                    <h3 className={styles.title}>{item.headline}</h3>
                    <p className={styles.subtitle}>{item.subHeadline}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
      <div className={styles.contents}>
        <div className={styles.content}>
          <h1 className={styles.title}>{t('The Advantages of Bus Travel')}</h1>
          <p className={styles.text}>
            {t(
              'Anyone who has been on a Trailways bus knows that it’s the only way to travel the American roads. Here are a few of the reasons why more and more passengers are choosing Trailways, even over the airlines.'
            )}
          </p>
        </div>
        <ul>
          <li>
            <span className={styles.bold}>{t('Limited wait time')}</span>
            <span className={styles.text}>
              {' - '}
              {t(
                'you don’t need to show up hours early for your Trailways departure. Please allow 30 minutes to ensure that you don’t miss your bus.'
              )}
            </span>
          </li>
          <li>
            <span className={styles.bold}>{t('Low stress')}</span>
            <span className={styles.text}>
              {' - '}
              {t(
                'There’s no reason to panic about being late for your departure. Chances are excellent that we can get you on the next bus to your destination.'
              )}
            </span>
          </li>
          <li>
            <span className={styles.bold}>{t('Beautiful scenery')}</span>
            <span className={styles.text}>
              {' - '}
              {t('With large panoramic windows, you won’t miss the incredible scenery rolling by.')}
            </span>
          </li>
          <li>
            <span className={styles.bold}>{t('Catch up on sleep')}</span>
            <span className={styles.text}>
              {' - '}
              {t(
                'If you’re going on a longer trip, you can catch your eight hours on the bus. You can arrive refreshed and avoid paying for a night at the hotel. The savings might even cover the price of your ticket.'
              )}
            </span>
          </li>
          <li>
            <span className={styles.bold}>{t('Road Safety')}</span>
            <span className={styles.text}>
              {' - '}
              {t('Trailways has an excellent safety record. We take every measure to keep it that way.')}
            </span>
          </li>
        </ul>
      </div>
      <div className={styles['content-1']}>
        <h1 className={styles.title}>{t('Charter Bus Rental')}</h1>
        <p
          className={styles.text}
          dangerouslySetInnerHTML={{
            __html: t(
              'Plan a group trip with Trailways today. Trailways has one of the largest and safest fleets of vehicles in North America. To request a quote, call (877) 467- 3346 or use our convenient <a>online booking engine</a>. Tell us the date, departure location, destination, and the expected number of passengers, and we’ll take care of the rest.',
              { url: '/charter-bus-rental/' }
            )
          }}
        />
      </div>
      <div className={styles['content-1']}>
        <h1 className={styles.title}>{t('Trip Packages')}</h1>
        <p
          className={styles.text}
          dangerouslySetInnerHTML={{
            __html: t(
              'We routinely partner with area destinations to offer our passengers the best prices on <a>package deals</a>. When you combine bus travel with events and attractions, you can realize amazingly low savings. And getting there is part of the fun!',
              { url: '/packages/' }
            )
          }}
        />
      </div>
      <div className={styles['free-rides']}>
        <div className={styles.section}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t('Free Rides? Yes, please!')}</h2>
            <div className={styles.subtitles}>
              <p className={styles.text}>
                {t(
                  "Redeemable for valuable discounts or even complimentary bus tickets. Don't wait any longer – join now and start enjoying the benefits."
                )}
              </p>
            </div>
          </div>
          <div className={styles.join}>
            <Button
              primary={true}
              type="submit"
              onClick={() =>
                (window.location.href = `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/account/createaccount`)
              }
              aria-describedby="restrictions-text"
              id="gtm-join_today"
            >
              {t('Join Today!')}
            </Button>
            <p className={styles.text} id="restrictions-text">
              {t('Restrictions apply')}*
            </p>
          </div>
        </div>
        <div className={styles.image}>
          <div className={styles['image-wrapper']}>
            <Image
              alt="Smiling couple with camera and brochure, planning their travel adventures for the day"
              fill
              src={freeRide.src}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePageNewRoadsToTravel;
